import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import {
  FarmRiskType,
  FarmScheduleCoverageType,
  FarmScheduleCoverageTypesByState,
  lookupsJson,
  StructureCategory
} from '@ourbranch/lookups';
import { observer } from 'mobx-react';
import { flatMap } from 'lodash-es';
import { useFormikContext, getIn } from 'formik';

import { brandConfig } from 'brand-config';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import { useToast } from 'core/components/toast';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import {
  InlandMarineForm,
  UnscheduledPersonalPropertyForm,
  FarmIncomeCoverageForm,
  CosmeticDamageExclusion
} from '../sub-risks';
import { Banner } from 'common/components/banner';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { NotificationCard } from 'core/components/notification-card';
import { getHeatingOptionsByOccupancy } from 'common/helpers/farm-helpers';
import { Tooltip } from 'core/components/tooltip';
import editIcon from 'core/assets/svg/edit.svg';
import { formatRiskAddress } from '../../helpers';
import { BrandFormField } from 'common/components/farm/brand-form-field';

import useStyles from '../risk.styles';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const ctOptions = lookupsJson.farm.agriculturalConstructionType;
const barnRtOptions = lookupsJson.farm.barnRoofType;
const occupancyUseTypeByStructureCategory = lookupsJson.farm.occupancyUseTypeByStructureCategory;
const buildingClassOptions = lookupsJson.farm.buildingClassOptions;
const buildingClassOptionsByStructureCategory = lookupsJson.farm.buildingClassOptionsByStructureCategory;
const structureCategoriesByState = lookupsJson.farm.structureCategoriesByState;
const allOtherOptions = lookupsJson.farm.deductibleAllOther;
const earthquakeDeductibleOptions = lookupsJson.farm.earthquakeDeductible.filter((d) => d.id !== 0.02);

const buildingTypes = flatMap(structureCategoriesByState);

const riskType = FarmRiskType.Fo6;

const FO6 = ({ item, index, fieldName, removeRisk, setRiskFieldnameToEdit, canRemoveRisk, isLastRisk }) => {
  const classes = useStyles();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const { setFieldValue, initialValues, errors } = useFormikContext();
  const toast = useToast();
  const address = item.address;
  const hasBeenRated = !!item.id;
  const state = address?.state;
  const availableCoverageTypes = FarmScheduleCoverageTypesByState[state] || [];

  // Get building type from the item
  const buildingType = item.building?.buildingType;
  const buildingTypeLabel = buildingTypes.find((option) => option.id === buildingType)?.value;

  // Check if structure category is Barns/Outbuildings, Storage Grain, or Poultry Building
  const isBarnOutbuilding = buildingType === StructureCategory.BarnsOutbuildings;
  const isStorageGrain = buildingType === StructureCategory.SilosGrainBins;
  const isPoultryBuilding = [
    StructureCategory.PoultryBrooder,
    StructureCategory.PoultryIncubator,
    StructureCategory.PoultryOther
  ].includes(buildingType);

  const currentHeatingOptions = getHeatingOptionsByOccupancy(buildingType, item.building?.occupancy);

  const isSelectedHeatingSourceUnitCountBased = currentHeatingOptions.find(
    (option) => option.id === item.building?.primaryHeatingSource
  )?.unitCountBased;

  // Check field requirements based on occupancy type
  const isTowerOccupancy = item.building?.occupancy === 'GHT';
  const shouldEnableDiameterHeight = isTowerOccupancy;
  const shouldEnableSqFt = item.building?.occupancy && !isTowerOccupancy;

  const checkIfCoverageIsEnabled = (coverageName) =>
    !brandConfig.farm[state]?.[riskType]?.hiddenCoverages?.includes(coverageName);

  useEffect(() => {
    if (isPoultryBuilding) {
      setFieldValue(`${fieldName}.building.occupancy`, 'PCH');
    }
  }, []);

  const contentRight = canRemoveRisk ? (
    <Field
      id={fieldName}
      name={fieldName}
      type="remove"
      disabled={disableRiskForm}
      onClick={() => {
        removeRisk(index);
      }}
    />
  ) : undefined;

  const initialCoverageE = getIn(initialValues, `${fieldName}.coverages.coverageE`);
  const initialBuilding = getIn(initialValues, `${fieldName}.building`);
  // Use ref to track coverage updates to avoid stale closure issues
  const coverageHasBeenUpdatedRef = useRef(false);

  // Update ref when coverage changes
  useEffect(() => {
    coverageHasBeenUpdatedRef.current = initialCoverageE !== item.coverages?.coverageE;
  }, [initialCoverageE, item.coverages?.coverageE]);

  const toastRCEWarning = () => {
    if (coverageHasBeenUpdatedRef.current) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  };

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">FO6 Structure: {buildingTypeLabel}</Label>
            <div className={classes.divider} />
            <Label type="infoSubtitleSmall">{item.description}</Label>
            <IconButton onClick={() => setRiskFieldnameToEdit(fieldName)} className={classes.editButton}>
              <img className={classes.editIcon} src={editIcon} alt="Edit row" />
            </IconButton>
          </div>
          <div className={classes.headerRow}>
            <Label type="infoSubtitleSmallLight">{formatRiskAddress(address)}</Label>
          </div>
        </div>
      }
      contentRight={contentRight}
      initialOpen={!disableRiskForm || isLastRisk}
      // @TODO: need to figure out good way to handle collapsing inactive forms and scrolling into active ones
      // initialOpen={activeForm ? !disableRiskForm : index === 0}
      // open={!disableRiskForm}
    >
      {!hasBeenRated && (
        <Banner
          variant="warning"
          title="Data for this dwelling or structure isn't available—please enter the details manually."
          subtitle="This information won't auto-fill, even if you update the offer."
        />
      )}
      <Grid container className={classes.riskCard} spacing={2}>
        <Grid item xs={12}>
          <Label type="infoTitleSmall">Property</Label>
        </Grid>

        {isBarnOutbuilding && (
          <>
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.occupancy`}
              name={`${fieldName}.building.occupancy`}
              mode="light"
              label="Occupancy or use type"
              options={occupancyUseTypeByStructureCategory[buildingType]}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.occupancy) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              format={{
                allowNegative: false
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.sqFt`}
              name={`${fieldName}.building.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.sqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.buildingQuality`}
              name={`${fieldName}.building.buildingQuality`}
              mode="light"
              label="Quality"
              options={bqOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.roofShape`}
              name={`${fieldName}.building.roofShape`}
              mode="light"
              label="Roof shape"
              options={barnRtOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.roofShape) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.constructionType`}
              name={`${fieldName}.building.constructionType`}
              mode="light"
              label="Construction type"
              options={ctOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.constructionType) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.buildingClass`}
              name={`${fieldName}.building.buildingClass`}
              mode="light"
              label="Building class"
              options={buildingClassOptionsByStructureCategory[buildingType] || buildingClassOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingClass) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="checkbox"
              id={`${fieldName}.building.structureOpenSides`}
              name={`${fieldName}.building.structureOpenSides`}
              mode="light"
              label="Has open sides?"
              className={classes.openSidesCheckbox}
              disabled={disableRiskForm || ![2, 3].includes(item.building?.buildingClass)}
              onChange={(val) => {
                if (val !== initialBuilding.structureOpenSides) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4}>
              <LabelTooltip
                label="Fuel Burning Appliances"
                tooltip={{
                  label: 'More Info',
                  onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
                }}
                mode="light"
              >
                <Field
                  disabled={disableRiskForm}
                  type="numeric"
                  id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  mode="light"
                  format={{
                    allowNegative: false
                  }}
                  onChange={(val) => {
                    if (val !== initialBuilding.numSolidFuelBurningAppliances) {
                      toastRCEWarning();
                    }
                  }}
                />
              </LabelTooltip>
            </Grid>
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.primaryHeatingSource`}
              name={`${fieldName}.building.primaryHeatingSource`}
              mode="light"
              label="Primary heating"
              options={currentHeatingOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.primaryHeatingSource) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.numHeatingSources`}
              name={`${fieldName}.building.numHeatingSources`}
              mode="light"
              label="Heating units"
              disabled={disableRiskForm || !isSelectedHeatingSourceUnitCountBased}
              helperText="Acceptable range: 1-1000"
              onChange={(val) => {
                if (val !== initialBuilding.numHeatingSources) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {isStorageGrain && (
          <>
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.occupancy`}
              name={`${fieldName}.building.occupancy`}
              mode="light"
              label="Occupancy or use type"
              options={occupancyUseTypeByStructureCategory[buildingType]}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.occupancy) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.diameter`}
              name={`${fieldName}.building.diameter`}
              mode="light"
              label="Diameter (ft)"
              disabled={disableRiskForm || !shouldEnableDiameterHeight}
              onChange={(val) => {
                if (val !== initialBuilding.diameter) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.height`}
              name={`${fieldName}.building.height`}
              mode="light"
              label="Height (ft)"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.height) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.sqFt`}
              name={`${fieldName}.building.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={disableRiskForm || !shouldEnableSqFt}
              onChange={(val) => {
                if (val !== initialBuilding.sqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.buildingQuality`}
              name={`${fieldName}.building.buildingQuality`}
              mode="light"
              label="Quality"
              options={bqOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.constructionType`}
              name={`${fieldName}.building.constructionType`}
              mode="light"
              label="Construction type"
              options={ctOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.constructionType) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.buildingClass`}
              name={`${fieldName}.building.buildingClass`}
              mode="light"
              label="Building class"
              options={buildingClassOptionsByStructureCategory[buildingType] || buildingClassOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingClass) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="numeric"
              id={`${fieldName}.building.bushelCapacity`}
              name={`${fieldName}.building.bushelCapacity`}
              mode="light"
              label="Bushel capacity"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.bushelCapacity) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {isPoultryBuilding && (
          <>
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.sqFt`}
              name={`${fieldName}.building.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.sqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.buildingQuality`}
              name={`${fieldName}.building.buildingQuality`}
              mode="light"
              label="Quality"
              options={bqOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.constructionType`}
              name={`${fieldName}.building.constructionType`}
              mode="light"
              label="Construction type"
              options={ctOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.constructionType) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4}>
              <LabelTooltip
                label="Fuel Burning Appliances"
                tooltip={{
                  label: 'More Info',
                  onHoverText: `Count appliances that burn fuel like gas, propane, oil, or wood. Common examples: furnace, boiler, water heater, fireplace, wood stove, gas oven, gas dryer, or backup generators. Electric-only appliances don't count.`
                }}
                mode="light"
              >
                <Field
                  type="numeric"
                  id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  mode="light"
                  disabled={disableRiskForm}
                  format={{
                    allowNegative: false
                  }}
                  onChange={(val) => {
                    if (val !== initialBuilding.numSolidFuelBurningAppliances) {
                      toastRCEWarning();
                    }
                  }}
                />
              </LabelTooltip>
            </Grid>
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.primaryHeatingSource`}
              name={`${fieldName}.building.primaryHeatingSource`}
              mode="light"
              label="Primary heating"
              options={currentHeatingOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.primaryHeatingSource) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.numHeatingSources`}
              name={`${fieldName}.building.numHeatingSources`}
              mode="light"
              label="Heating units"
              disabled={disableRiskForm || !isSelectedHeatingSourceUnitCountBased}
              helperText="Acceptable range: 1-1000"
              onChange={(val) => {
                if (val !== initialBuilding.numHeatingSources) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {(hasBeenRated || coverageHasBeenUpdatedRef.current) && (
          <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
            <Grid container xs={8} className={classes.rceValues}>
              <Label>Our estimated cost to replace this structure</Label>
              <Tooltip
                label="More info"
                text="RCE, or Replacement Cost Estimate, is our estimated cost for replacing this dwelling or structure in the case of a total loss. This number is calculated using Home360 as well as other data vendors. The maximum amount we will cover in the case of a total loss is defined by the Dwelling limit."
              />
            </Grid>
            <Grid container xs={4} justifyContent="flex-end" className={classes.rceValues}>
              <Label>RCE</Label>
              <Label type="infoValueBold">
                {item.building?.replacementCostEstimate?.total
                  ? `$${item.building.replacementCostEstimate.total.toLocaleString('en-US')}`
                  : 'N/A'}
              </Label>
            </Grid>
          </NotificationCard>
        )}
      </Grid>

      {hasBeenRated && (
        <Grid container spacing={2} className={classes.coverageCard}>
          <Grid item xs={12} className={classes.bottomMargin}>
            <Label type="formSubTitle">Base coverages</Label>
          </Grid>
          <Grid item xs={12}>
            <LabelTooltip
              label="Property Coverage (E)"
              tooltip={{
                label: 'More Info',
                onHoverText:
                  'Coverage for buildings and structures on the property, protecting against damage from covered perils.'
              }}
            >
              <Field
                id={`${fieldName}.coverages.coverageE`}
                name={`${fieldName}.coverages.coverageE`}
                type="numeric"
                mode="dark"
                format={{ thousandSeparator: true, prefix: '$' }}
                disabled={disableRiskForm}
                description={
                  item.coverages.canEditCoverageAmount && !getIn(errors, `${fieldName}.coverages.coverageE`)
                    ? `You may adjust this from $${initialCoverageE * 0.8} (80% RCE) to $${initialCoverageE * 10} (1,000% RCE).`
                    : undefined
                }
              />
            </LabelTooltip>
          </Grid>

          <div className={classes.coverageToggleContainer}>
            <Grid container xs={12} justifyContent="space-between">
              <Field
                id={`${fieldName}.coverages.canEditCoverageAmount`}
                name={`${fieldName}.coverages.canEditCoverageAmount`}
                type="switch"
                mode="dark"
                label="Adjust scheduled property limit"
                tooltipText="By default (and when toggled off), the scheduled property limit matches our estimated replacement cost. If you have an external estimate, you can adjust the limit here. Supporting documentation will be required."
                tooltipLabel="More Info"
                xs={8}
                disabled={coverageHasBeenUpdatedRef.current}
              />
              <Grid className={classes.linkAndIcon}>
                <a
                  rel="noreferrer noopener"
                  target="_blank"
                  href="https://www.360-value.com/apps/iv/ui/#/login"
                  className={classes.link}
                >
                  ISO 360 Login
                  <img src={externalLinkIcon} alt="link to ISO 360 login" />
                </a>
              </Grid>

              {item.coverages.canEditCoverageAmount && (
                <Grid item xs={12} className={classes.adjustCoverageSubContainer}>
                  <Field
                    id={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                    name={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                    type="textarea"
                    mode="dark"
                    label="Description of reason for adjusting scheduled property limit (required)"
                    xs={12}
                    rows={3}
                  />
                </Grid>
              )}
            </Grid>
          </div>

          <Grid item xs={12} className={classes.bottomMargin}>
            <Label type="formSubTitle" className={classes.sectionDivider}>
              Deductibles
            </Label>
          </Grid>
          <Field
            xs={checkIfCoverageIsEnabled('deductibleEarthquake') ? 6 : 12}
            id={`${fieldName}.coverages.deductible`}
            name={`${fieldName}.coverages.deductible`}
            type="select"
            options={allOtherOptions}
            mode="dark"
            label="Building Deductible"
            disabled={disableRiskForm}
          />
          <BrandFormField
            xs={6}
            id={`${fieldName}.coverages.deductibleEarthquake`}
            name={`${fieldName}.coverages.deductibleEarthquake`}
            type="select"
            options={earthquakeDeductibleOptions}
            mode="dark"
            label="Earthquake Deductible"
            disabled={disableRiskForm}
            riskType={riskType}
            state={state}
          />

          <Grid item xs={12} className={classes.bottomMargin}>
            <Label type="formSubTitle" className={classes.sectionDivider}>
              Optional Coverages
            </Label>
          </Grid>
          <Field
            xs={6}
            name={`${fieldName}.coverages.specialFormCoverage`}
            id={`${fieldName}.coverages.specialFormCoverage`}
            type="checkbox"
            label="Special form coverage"
            mode="dark"
            disabled={disableRiskForm}
          />
          <Field
            xs={6}
            name={`${fieldName}.coverages.hasReplacementCostCoverage`}
            id={`${fieldName}.coverages.hasReplacementCostCoverage`}
            type="checkbox"
            label="Replacement cost provision"
            mode="dark"
            disabled={disableRiskForm}
          />

          <Field
            xs={6}
            name={`${fieldName}.coverages.repairRebuildRequirement`}
            id={`${fieldName}.coverages.repairRebuildRequirement`}
            type="checkbox"
            label="Repair or rebuilding requirement"
            mode="dark"
            disabled
          />
          <Field
            xs={6}
            name={`${fieldName}.coverages.buildingMaterialTheft`}
            id={`${fieldName}.coverages.buildingMaterialTheft`}
            type="checkbox"
            label="Theft of building materials"
            mode="dark"
            disabled={disableRiskForm}
          />
          <BrandFormField
            xs={6}
            name={`${fieldName}.coverages.hasEarthquakeExteriorVeneerCoverage`}
            id={`${fieldName}.coverages.hasEarthquakeExteriorVeneerCoverage`}
            type="checkbox"
            label="Earthquake exterior veneer coverage"
            mode="dark"
            disabled
            riskType={riskType}
            state={state}
          />

          <Field
            xs={6}
            name={`${fieldName}.coverages.hasSnowWeightCoverage`}
            id={`${fieldName}.coverages.hasSnowWeightCoverage`}
            type="checkbox"
            label="Weight of snow and ice coverage"
            mode="dark"
            disabled={disableRiskForm}
          />

          {/* unscheduled personal property */}
          <UnscheduledPersonalPropertyForm fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
          {/* scheduled personal property (inland marine coverage) */}
          {availableCoverageTypes.includes(FarmScheduleCoverageType.SPP) && (
            <InlandMarineForm
              fieldName={`${fieldName}.coverages.scheduleCoverages`}
              disabled={disableRiskForm}
              state={state}
              riskType={FarmRiskType.Fo6}
              coverages={item.coverages}
            />
          )}
          {/* farm income coverage */}
          <FarmIncomeCoverageForm fieldName={`${fieldName}.coverages.scheduleCoverages`} disabled={disableRiskForm} />
          {/* cosmetic damage exclusion */}
          <CosmeticDamageExclusion fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />
        </Grid>
      )}
    </CollapsibleCard>
  );
};

FO6.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  removeRisk: PropTypes.func.isRequired,
  setRiskFieldnameToEdit: PropTypes.func.isRequired,
  isLastRisk: PropTypes.bool.isRequired
};

export default observer(withDatePicker(FO6));
